import { useCallback, useRef, useState } from 'react';

import { geolocationLibraryIsLoaded, loadGeolocationLibrary } from 'utils/geolocation';

type GooglePlacesService = google.maps.places.PlacesService;

/**
 * Returns PlacesService getter function.
 */
export const usePlacesService = (map: google.maps.Map | null = null) => {
  const placesNoMap = useRef<GooglePlacesService>();
  const placesWithMap = useRef<GooglePlacesService>();

  // Need to track internally when geo is loaded
  // so we can re-render to re-create the placesService callback
  const [geoLibraryLoaded, setGeoLibraryLoaded] = useState<boolean>(false);

  /*
   * Returns a places service instance
   */
  const placesService = useCallback((): GooglePlacesService | undefined => {
    // check if geolocation library is loaded and load if not
    if (!geolocationLibraryIsLoaded() && !geoLibraryLoaded) {
      loadGeolocationLibrary().then(() => setGeoLibraryLoaded(true));
      return undefined;
    }

    // return already stored places Service
    if (!map && placesNoMap.current) {
      return placesNoMap.current;
    } else if (placesWithMap.current) {
      return placesWithMap.current;
    }

    // setup places services
    if (!map) {
      /*
       * Note: When we don't have a map, we setup a new PlacesService with just "a div"
       * Ref: https://stackoverflow.com/a/14345546
       */
      placesNoMap.current = new window.google.maps.places.PlacesService(
        document.createElement('div')
      );
      return placesNoMap.current;
    } else {
      placesWithMap.current = new window.google.maps.places.PlacesService(map);
      return placesWithMap.current;
    }
  }, [map, geoLibraryLoaded, setGeoLibraryLoaded]);

  return placesService;
};
